import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";

import { Client } from "@twilio/conversations";
import { AttachIcon } from "@twilio-paste/icons/esm/AttachIcon";
import { Box, Button } from "@twilio-paste/core";
import { useTheme } from "@twilio-paste/theme";
import { Text } from "@twilio-paste/text";

import { actionCreators } from "../../store";
import { MAX_FILE_SIZE } from "../../constants";
import { getTypingMessage, unexpectedErrorNotification } from "../../helpers";
import MessageInput from "./MessageInput";
import SendMessageButton from "./SendMessageButton";
import { ReduxConversation } from "../../store/reducers/convoReducer";
import { getSdkConversationObject } from "../../conversations-objects";
import { ReduxMessage } from "../../store/reducers/messageListReducer";

interface SendMessageProps {
  convoSid: string;
  client: Client;
  messages: ReduxMessage[];
  convo: ReduxConversation;
  typingData: string[];
  droppedFiles: File[];
  isMobile?: boolean;
}

const MessageInputField: React.FC<SendMessageProps> = (
  props: SendMessageProps
) => {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  // needed to clear input type=file
  const [filesInputKey, setFilesInputKey] = useState<string>("input-key");

  const theme = useTheme();
  const typingInfo = getTypingMessage(props.typingData);

  const dispatch = useDispatch();
  const { addNotifications } = bindActionCreators(actionCreators, dispatch);

  useEffect(() => {
    setMessage("");
    setFiles([]);
    setFilesInputKey(Date.now().toString());
  }, [props.convo]);

  useEffect(() => {
    if (!files.length) {
      setFilesInputKey(Date.now().toString());
    }
  }, [files]);

  useEffect(() => {
    const abortController = new AbortController();
    setFiles(props.droppedFiles);
    return () => {
      abortController.abort();
    };
  }, [props.droppedFiles]);

  const sdkConvo = useMemo(
    () => getSdkConversationObject(props.convo),
    [props.convo.sid]
  );

  const onFilesChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { files: assets } = event.target;
    if (!assets?.length) {
      return;
    }

    const validFiles = Array.from(assets).filter(
      ({ size }) => size < MAX_FILE_SIZE + 1
    );

    if (validFiles.length < assets.length) {
      // TODO: show error
    }

    setFiles([...files, ...validFiles]);
  };

  const onFileRemove = (file: string) => {
    const fileIdentityArray = file.split("_");
    const fileIdentity = fileIdentityArray
      .slice(0, fileIdentityArray.length - 1)
      .join();
    const existentFiles = files.filter(
      ({ name, size }) =>
        name !== fileIdentity &&
        size !== Number(fileIdentityArray[fileIdentityArray.length - 1])
    );

    setFiles(existentFiles);
  };

  const onMessageSend = async () => {
    if (message.length == 0 && files.length == 0) {
      return;
    }

    const { convo } = props;
    const sdkConvo = getSdkConversationObject(convo);

    const newMessageBuilder = sdkConvo.prepareMessage().setBody(message);

    // const newMessage: ReduxMessage = {
    //   author: client.user.identity,
    //   body: message,
    //   attributes: {},
    //   dateCreated: currentDate,
    //   index: -1,
    //   participantSid: "",
    //   sid: "-1",
    //   aggregatedDeliveryReceipt: null,
    //   attachedMedia: [],
    // } as ReduxMessage;

    for (const file of files) {
      const fileData = new FormData();
      fileData.set(file.name, file, file.name);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // newMessage.attachedMedia.push({
      //   sid: key + "",
      //   size: file.size,
      //   filename: file.name,
      //   contentType: file.type,
      // });
      // addAttachment(convo.sid, "-1", key + "", file);
      newMessageBuilder.addMedia(fileData);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // upsertMessages(convo.sid, [newMessage]);
    setMessage("");
    setFiles([]);
    const messageIndex = await newMessageBuilder.build().send();

    try {
      await sdkConvo.advanceLastReadMessageIndex(messageIndex ?? 0);
    } catch (e) {
      unexpectedErrorNotification(e.message, addNotifications);
      throw e;
    }
  };

  return (
    <Box
      display="flex"
      flexBasis="60px"
      flexGrow={10}
      flexDirection="column"
      borderTopStyle="solid"
      borderTopWidth="borderWidth10"
      style={{
        borderTopColor: theme.borderColors.colorBorderWeak,
        backgroundColor: props.isMobile
          ? "#EEF3FF"
          : theme.backgroundColors.colorBackgroundBody,
      }}
    >
      <Box
        paddingBottom={props.isMobile ? "space10" : "space20"}
        paddingTop={props.isMobile ? "space20" : "space50"}
        paddingLeft={props.isMobile ? "space40" : "space150"}
        hidden={!props.typingData.length}
      >
        <Text
          as="p"
          color="colorTextIcon"
          style={
            props.isMobile
              ? {
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 10px",
                  borderRadius: 999,
                  backgroundColor: "rgba(6, 3, 58, 0.06)",
                  fontSize: 12,
                  fontWeight: 600,
                }
              : undefined
          }
        >
          {typingInfo}
        </Text>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        height="100%"
        flexGrow={10}
        flexWrap="nowrap"
        alignItems="flex-end"
        paddingBottom={props.isMobile ? "space30" : "space30"}
        paddingTop={props.isMobile ? "space10" : "space40"}
        paddingLeft={props.isMobile ? "space30" : undefined}
        paddingRight={props.isMobile ? "space30" : undefined}
        style={
          props.isMobile
            ? {
                alignItems: "flex-end",
                gap: 10,
                backgroundColor: "#FFFFFF",
                borderRadius: 22,
                border: "1px solid rgba(57, 71, 98, 0.12)",
                boxShadow: "0 10px 24px rgba(6, 3, 58, 0.08)",
                margin: "0 10px 12px 10px",
              }
            : undefined
        }
      >
        <Box
          paddingBottom={props.isMobile ? "space20" : "space30"}
          paddingLeft={props.isMobile ? "space20" : "space50"}
          paddingRight={props.isMobile ? "space0" : "space10"}
          paddingTop={props.isMobile ? "space20" : "space20"}
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          alignItems="start"
        >
          {props.isMobile ? (
            <label
              htmlFor="file-input"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                backgroundColor: "#EEF3FF",
                color: "#2D4A9F",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <AttachIcon
                decorative={true}
                title="Attach file"
                size="sizeIcon40"
              />
            </label>
          ) : (
            <Button variant="link">
              <label htmlFor="file-input">
                <AttachIcon
                  decorative={true}
                  title="Attach file"
                  size="sizeIcon50"
                />
              </label>
            </Button>
          )}
          <input
            id="file-input"
            key={filesInputKey}
            type="file"
            style={{ display: "none" }}
            onChange={onFilesChange}
          />
        </Box>
        <Box
          paddingRight={props.isMobile ? "space0" : "space50"}
          flexGrow={10}
          flexShrink={1}
          minWidth={0}
          paddingBottom={props.isMobile ? "space10" : undefined}
          paddingTop={props.isMobile ? "space10" : undefined}
        >
          <MessageInput
            assets={files}
            message={message}
            onChange={(e: string) => {
              sdkConvo.typing();
              setMessage(e);
            }}
            onEnterKeyPress={async () => {
              await onMessageSend();
            }}
            onFileRemove={onFileRemove}
            isMobile={props.isMobile}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          alignItems="start"
          flexShrink={0}
          minWidth={props.isMobile ? undefined : "92px"}
          paddingRight={props.isMobile ? "space20" : undefined}
          paddingBottom={props.isMobile ? "space20" : undefined}
        >
          <SendMessageButton
            message={message}
            disabled={!message && files.length === 0}
            onClick={onMessageSend}
            isMobile={props.isMobile}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MessageInputField;
